import React, { useEffect } from 'react'
import { useGlobalTemplatePresetInputParameters } from '../../hooks'
import { TemplateInputParameters } from '../../../../../../models/AiFeature'
import { PresetTemplateConfigForm } from '../PresetTemplateConfigForm'
import { YesNoCancelModal } from '../../../../../../components/Modal/YesNoCancelModal'
import { Button } from '../../../../../../components/Button/Button'
import { PresetConfigLoader } from '../../Common/PresetConfigLoader'
import { useUserTemplateConfigForm } from './hooks'
import {
  InputParameterHierarchyType,
  MeetingTemplateInputParametersHierarchyConfigComponentProps,
} from '../../../../../../components/InputParameterHierarchy/model'
import { useMeetingTemplateEffectiveInputParameters, useInputParametersUpdateScope } from '../../../../../../components/InputParameterHierarchy/hooks'
import { useGetPresetByIdQuery } from '../../../../../../store/ai/ai'
import { useAppSelector } from '../../../../../../store/hooks'
import { selectAccountId } from '../../../../../../store/auth/authSlice'
import { AiFeaturePreset } from '../../../../../../models/AiFeaturePreset'

export const MeetingTemplateGlobalPresetTemplateConfigForm: React.FC<MeetingTemplateInputParametersHierarchyConfigComponentProps> = ({
  typeId,
  onHide,
  meetingTemplateId,
  showCustomizationSetting,
}) => {
  const accountId = useAppSelector(selectAccountId)
  const { data: presetData } = useGetPresetByIdQuery({ accountId, presetId: typeId })

  const { presetInputParameters, presetDefaultInputParameters } = useGlobalTemplatePresetInputParameters(presetData?.data)

  const { inputParameters, parentInputParameters, updateLoading, updateInputParameters, isCustomized } = useMeetingTemplateEffectiveInputParameters(
    InputParameterHierarchyType.AI_FEATURE_PRESET,
    typeId,
    presetInputParameters,
    undefined,
    meetingTemplateId,
  )

  if (!presetData || !presetData.data || !inputParameters || !presetDefaultInputParameters || !parentInputParameters) {
    return <PresetConfigLoader />
  }

  const { example_1 = '', example_2 = '', template } = inputParameters
  const { example_1: defaultExample_1 = '', example_2: defaultExample_2 = '', template: defaultTemplate } = presetDefaultInputParameters
  const { example_1: parentExample_1 = '', example_2: parentExample_2 = '', template: parentTemplate } = parentInputParameters

  return (
    <MeetingTemplateGlobalTemplateConfigFormBody
      preset={presetData.data}
      inputParameters={{ example_1, example_2, template }}
      defaultInputParameters={{ example_1: defaultExample_1, example_2: defaultExample_2, template: defaultTemplate }}
      parentInputParameters={{ example_1: parentExample_1, example_2: parentExample_2, template: parentTemplate }}
      onSave={updateInputParameters}
      onHide={onHide}
      updateLoading={updateLoading}
      isCustomized={isCustomized}
      showCustomizationSetting={showCustomizationSetting}
    />
  )
}

interface MeetingTemplateGlobalTemplateConfigFormBodyProps {
  preset: AiFeaturePreset
  defaultInputParameters: TemplateInputParameters
  parentInputParameters: TemplateInputParameters
  inputParameters: TemplateInputParameters
  onSave: (inputParameters: TemplateInputParameters, isCustomized: boolean) => Promise<void>
  onHide: () => void
  updateLoading: boolean
  isCustomized: boolean
  showCustomizationSetting: boolean
}

const MeetingTemplateGlobalTemplateConfigFormBody: React.FC<MeetingTemplateGlobalTemplateConfigFormBodyProps> = ({
  preset,
  defaultInputParameters,
  parentInputParameters,
  inputParameters,
  onSave,
  onHide,
  updateLoading,
  isCustomized,
  showCustomizationSetting,
}) => {
  const { hasIpHierarchyAdminScope } = useInputParametersUpdateScope()
  const disabled = !hasIpHierarchyAdminScope

  const {
    editedInputParameters,
    isChanged,
    customized,
    showConfirmation,
    shouldReset,
    isGenerating,
    showGenerate,
    setEditedInputParameters,
    setShouldReset,
    setShowConfirmation,
    setShowGenerate,
    setGenerating,
    handleChangeCustomized,
    handleChangeExample,
    handleChangeTemplate,
    handleClickBack,
    handleClickCancel,
    handleClickSave,
    checkForChange,
  } = useUserTemplateConfigForm(inputParameters, parentInputParameters, isCustomized, onSave, onHide, disabled)

  useEffect(() => {
    checkForChange()
    setShouldReset(false)
  }, [checkForChange])

  return (
    <>
      <YesNoCancelModal
        show={showConfirmation}
        onHide={() => setShowConfirmation(false)}
        message={'Do you want to save your changes?'}
        onYes={() => {
          onSave(editedInputParameters, customized)
          onHide()
        }}
        onNo={() => {
          setEditedInputParameters(inputParameters)
          onHide()
        }}
      />
      <PresetTemplateConfigForm
        preset={preset}
        inputParameters={inputParameters}
        defaultInputParameters={defaultInputParameters}
        parentInputParameters={parentInputParameters}
        editedInputParameters={editedInputParameters}
        showCustomizationSetting={showCustomizationSetting}
        customized={customized}
        shouldReset={shouldReset}
        shouldShowGenerate={showGenerate}
        showGenerate={() => setShowGenerate(true)}
        onChangeTemplate={handleChangeTemplate}
        onChangeExample={handleChangeExample}
        onGenerate={setGenerating}
        onChangeCustomized={handleChangeCustomized}
        disabled={disabled}
      />
      <div className='d-flex justify-content-between w-100 mt-3'>
        <Button variant='borderless' onClick={handleClickBack}>
          {showGenerate ? 'Back' : 'Close'}
        </Button>
        {hasIpHierarchyAdminScope && (
          <div className='d-flex gap-2'>
            <Button variant='secondary' onClick={handleClickCancel} disabled={!isChanged} loading={updateLoading || isGenerating}>
              Cancel
            </Button>
            <Button variant='primary' onClick={handleClickSave} disabled={!isChanged} loading={updateLoading || isGenerating}>
              Save
            </Button>
          </div>
        )}
      </div>
    </>
  )
}
